// External Libraries
import { Formik, Form, FormikErrors } from "formik";
import * as Yup from "yup";
import wait from "wait";
import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Portal } from "react-portal";

// Shared/Utilities
import { clsxMerge } from "shared/lib/helpers";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { DayJs } from "shared/lib/helpers/date";
import {
  DISPOSITION_STATUSES,
  DISPOSITIONS_GLENX,
  DISPOSITIONS_V3,
  NEXT_TOUCH_TIME_OPTIONS,
  PROSPECT_DISPOSITION_TYPES,
} from "shared/lib/constants/dispositions";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";
import { dd } from "@/helpers/datadog";

// Components
import { WIDGETS, widgets } from "@/widgets/index";
import {
  DATE_TIME_CALENDAR_MODAL_ID,
  DateTimeCalendarModal,
} from "@/modals/date-time-calendar-modal";
import { DispositionConversationRow } from "./conversation-row";
import { DispositionNoteRow } from "./note-row";
import { DispositionDecisionMakerRow } from "./decision-maker-row";
import { DispositionRow, isNextTouchTimeRequired } from "./disposition-row";
import { DispositionActionsRow } from "./actions-row";
import { DispositionSectionSearchRow } from "./search-row";
import { DispositionSectionHeader } from "./header";

// Hooks
import { useDispositionSubmission } from "./use-disposition-submission-hook";
import {
  useCallingContext,
  useInCallContext,
} from "@/hooks/dialer/use-dialer-context";

//  Constants
import {
  DISPOSITION_CONTROL_KEYS,
  MIN_CONVERSATION_TIME_SECONDS,
} from "./constants";
import { LOG_CATEGORIES } from "@/constants/logs";

// Interfaces
import {
  DispositionSectionPropsI,
  EmailFollowUpConfigByDispositionI,
  FormValidationSchemaI,
} from "./interface";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { GetShouldSendEmailRequestParamsI } from "@/api/routes/email";
import { useShouldSendFollowUpEmail } from "@/modules/pipeline/account-details/queries/mutations";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { dispatchCustomEvent } from "@/helpers/events";
import { DispositionSubmitEventDataI } from "@/interfaces/custom-events";

// Contexts
import { useListTourContext } from "@/modules/tours/list/context";
import {
  DISPOSITION_OPTION_ID_SEPARATOR,
  DispositionSectionContextProvider,
  useDispositionSectionContext,
} from "@/modules/pipeline/account-details/sections/disposition-section/context";
import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";

const DISPO_SECTION_DATE_TIME_CALENDAR_MODAL_ID = `dispo-section-${DATE_TIME_CALENDAR_MODAL_ID}`;

const FormDefaultValidationSchema = Yup.object().shape({});

const isDispositionRequired = (
  disposition_type: ValueOfObjectFields<typeof PROSPECT_DISPOSITION_TYPES>
) =>
  ![
    PROSPECT_DISPOSITION_TYPES.BAD_DATA,
    PROSPECT_DISPOSITION_TYPES.NOT_RELEVANT,
    PROSPECT_DISPOSITION_TYPES.DO_NOT_CALL,
  ].includes(disposition_type);

const FormDispositionNurtureValidationSchema = Yup.object().shape({
  disposition: Yup.string().required(
    "Disposition is required to send feedback"
  ),
  next_touch_time: Yup.string().when("disposition", (disposition) => {
    return isNextTouchTimeRequired(disposition)
      ? Yup.string().required("Next touch time is required")
      : Yup.string().optional();
  }),
});

const isDisqualifiedDisposition = (disposition_type: any) =>
  [
    PROSPECT_DISPOSITION_TYPES.BAD_DATA,
    PROSPECT_DISPOSITION_TYPES.NOT_RELEVANT,
    PROSPECT_DISPOSITION_TYPES.DO_NOT_CALL,
  ].includes(disposition_type);

const FormDispositionValidationSchema = Yup.object().shape({
  disposition_type: Yup.string().required("You must select an option"),
  disposition: Yup.string().when("disposition_type", (disposition_type) => {
    return isDispositionRequired(disposition_type)
      ? Yup.string().required("Disposition is required to send feedback")
      : Yup.string().optional();
  }),
  next_touch_time: Yup.string().when(
    ["disposition_type", "disposition"],
    (disposition_type, disposition) => {
      return isDispositionRequired(disposition_type) &&
        isNextTouchTimeRequired(disposition)
        ? Yup.string().required("Next touch time is required")
        : Yup.string().optional();
    }
  ),

  /**
   * For meeting booked type only
   */
  meeting_time: Yup.string().when(
    ["disposition_type", "disposition"],
    (disposition_type, disposition) => {
      return isDispositionRequired(disposition_type) &&
        disposition === DISPOSITIONS_GLENX.MEETING_BOOKED
        ? Yup.string().required("Meeting time is required")
        : Yup.string().optional();
    }
  ),
  account_executive_id: Yup.string().when(
    ["disposition_type", "disposition"],
    (disposition_type, disposition) => {
      return isDispositionRequired(disposition_type) &&
        disposition === DISPOSITIONS_GLENX.MEETING_BOOKED
        ? Yup.string().required("Meeting taker is required")
        : Yup.string().optional();
    }
  ),
});

/**
 * NOTE
 *
 * The primary purpose of this component is to handle
 * default dropdown states based on selected values
 */
const DropdownsValueSetter = ({
  values,
  onChange,
  setValues,
  isDisposition,
  isDispositionType,
  setCustomNextTouchTime,
}: {
  values: FormValidationSchemaI;
  onChange?: (values: FormValidationSchemaI) => void;
  setValues: (
    values: SetStateAction<FormValidationSchemaI>,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<FormValidationSchemaI>>;
  isDisposition?: boolean;
  /**
   * If disposition type was preset from different flow
   * we need to make sure if disposition was selected
   * we have to unselect it
   */
  isDispositionType?: boolean;
  setCustomNextTouchTime: Dispatch<SetStateAction<string | undefined>>;
}) => {
  const resetDispositionObj = {
    disposition: undefined,
    custom_disposition_id: undefined,
    disposition_type: undefined,
    internalDispositionId: undefined,
  };

  useEffect(() => {
    onChange?.(values);
  }, [values]);

  useEffect(() => {
    if (values.internalDispositionId) {
      const [custom_disposition_id, disposition] =
        values.internalDispositionId.split(DISPOSITION_OPTION_ID_SEPARATOR);

      setValues({
        ...values,
        disposition,
        custom_disposition_id,
      });
    }
  }, [values.internalDispositionId]);

  useEffect(() => {
    setValues({
      ...values,
      ...resetDispositionObj,
      next_touch_time: undefined,
    });
  }, [isDisposition]);

  useEffect(() => {
    if (isDispositionType) {
      setValues({
        ...values,
        disposition_type: undefined,
      });
    }
  }, [isDispositionType]);

  useEffect(() => {
    switch (values.disposition_type) {
      case !isDispositionRequired(values.disposition_type as string)
        ? values.disposition_type
        : "undefined0":
        setValues({
          ...values,
          disposition: undefined,
          custom_disposition_id: undefined,
          internalDispositionId: undefined,
          next_touch_time: undefined,
        });
        break;
    }
  }, [values.disposition_type]);

  useEffect(() => {
    switch (values.disposition) {
      /**
       * Hacky way to squize switch case scenario into
       * handling specific similar condition
       */
      case [
        DISPOSITIONS_V3.DO_NOT_CALL,
        DISPOSITIONS_V3.QC_NOT_MET_FOR_ACCOUNT,
        DISPOSITIONS_V3.OTHER_DO_NOT_FOLLOW_UP,
      ].includes(values.disposition as string)
        ? values.disposition
        : "undefined0":
        setValues({
          ...values,
          next_touch_time: undefined,
        });
        break;

      case [
        DISPOSITIONS_V3.NOT_QUALIFIED_NEED as string,
        DISPOSITIONS_V3.NOT_QUALIFIED_TIMING,
        DISPOSITIONS_V3.PITCH_REJECTED,
        DISPOSITIONS_V3.INTRO_REJECTED,
      ].includes(values.disposition as string)
        ? values.disposition
        : "undefined1":
        setValues({
          ...values,
          next_touch_time: NEXT_TOUCH_TIME_OPTIONS.DAYS_45,
        });
        break;

      case [
        DISPOSITIONS_V3.INTERESTED as string,
        DISPOSITIONS_V3.OTHER_FOLLOW_UP,
      ].includes(values.disposition as string)
        ? values.disposition
        : "undefined2":
        setValues({
          ...values,
          next_touch_time: NEXT_TOUCH_TIME_OPTIONS.DAYS_3,
        });
        break;
    }

    if (values.disposition !== DISPOSITIONS_GLENX.MEETING_BOOKED) {
      setValues({
        ...values,
        meeting_time: undefined,
        account_executive_id: undefined,
      });
    }
  }, [values.disposition]);

  useEffect(() => {
    if (values.next_touch_time === NEXT_TOUCH_TIME_OPTIONS.CUSTOM) {
      modalHelpers.open(DISPO_SECTION_DATE_TIME_CALENDAR_MODAL_ID);
    } else {
      setCustomNextTouchTime?.(undefined);
    }
  }, [values.next_touch_time]);

  // CalendarForm already combined the selected date, time and timezone into
  // `date` (offset-bearing ISO) - re-combining it here would corrupt the
  // instant (GLE-8091).
  const handleSelectCustomDateTime = (date: string) => {
    setCustomNextTouchTime?.(DayJs(date).toISOString());

    modalHelpers.close(DISPO_SECTION_DATE_TIME_CALENDAR_MODAL_ID);
  };

  /**
   * We want to switch value to NONE if user select CUSTOM time
   * then canceled the modal
   */
  const handleOnClose = async () => {
    await wait(1000);

    setCustomNextTouchTime?.((customNextTouchTime) => {
      if (!customNextTouchTime) {
        setValues({ ...values, next_touch_time: "" });
      }

      return customNextTouchTime;
    });
  };

  return (
    <>
      {values.next_touch_time === NEXT_TOUCH_TIME_OPTIONS.CUSTOM && (
        // @ts-ignore
        <Portal>
          <DateTimeCalendarModal
            modalId={DISPO_SECTION_DATE_TIME_CALENDAR_MODAL_ID}
            title="Set Next Touch Time"
            descritpion="Specify date and time when to remind you to call back"
            onSuccess={handleSelectCustomDateTime}
            onClose={handleOnClose}
          />
        </Portal>
      )}
    </>
  );
};

export const DispositionSectionUI = ({
  className,
  actionControlKeys,
  buttonTexts,
  note,
  onSubmit,
  onChangeEmailFollowUpConfig,
}: DispositionSectionPropsI) => {
  const { dispositionOptions } = useDispositionSectionContext();
  const { contacts, campaign, emailFollowUpRequestParams } =
    useAccountDetailsContext();
  const { call } = useCallingContext();
  const { startAt, endAt, contact } = useInCallContext();

  const { isTourOpen: isTourMode } = useListTourContext();

  /**
   * Variations of disposition groups
   */
  const noConversationDispositionOptions = useMemo(() => {
    return dispositionOptions.filter((option) => {
      const [, , dispositionCategory] =
        (option.value as string)?.split(DISPOSITION_OPTION_ID_SEPARATOR) ?? [];

      return [DISPOSITION_STATUSES.COLD as string].includes(
        dispositionCategory
      );
    });
  }, [dispositionOptions]);

  const withConversationDispositionOptions = useMemo(() => {
    return dispositionOptions.filter((option) => {
      const [, , dispositionCategory] =
        (option.value as string)?.split(DISPOSITION_OPTION_ID_SEPARATOR) ?? [];

      return [
        ACCOUNT_DISPOSITION_STATUSES.NURTURE as string,
        ACCOUNT_DISPOSITION_STATUSES.DISQUALIFIED,
      ].includes(dispositionCategory);
    });
  }, [dispositionOptions]);

  const disqualifiedDispositionOptions = useMemo(() => {
    return dispositionOptions.filter((option) => {
      const [, , dispositionCategory] =
        (option.value as string)?.split(DISPOSITION_OPTION_ID_SEPARATOR) ?? [];

      return [ACCOUNT_DISPOSITION_STATUSES.DISQUALIFIED as string].includes(
        dispositionCategory
      );
    });
  }, [dispositionOptions]);

  /**
   * Disposition form values that are set in ValuesSetter component
   * should be modified only in that component
   * and observered here
   */
  const [observalbeValues, setObservalbeValues] =
    useState<FormValidationSchemaI>();

  /**
   * Values used outside formik values flow
   */
  const [isDispoConversation, setIsDispoConversation] = useState<boolean>();
  const [customNextTouchTime, setCustomNextTouchTime] = useState<string>();

  const { mutateAsync: shouldSendFollowUpEmailAsync } =
    useShouldSendFollowUpEmail();

  useEffect(() => {
    if (startAt && endAt) {
      if (
        Math.abs(DayJs(startAt).diff(endAt, "seconds")) >
        MIN_CONVERSATION_TIME_SECONDS
      ) {
        setIsDispoConversation(true);
      } else {
        setIsDispoConversation(false);
      }
    }
  }, [startAt, endAt]);

  useEffect(() => {
    if (isTourMode) {
      setIsDispoConversation(true);
    }
  }, [isTourMode]);

  const formInitialValues: FormValidationSchemaI = useMemo(
    () => ({
      disposition_type: undefined,
      custom_disposition_id: undefined,
      disposition: undefined,
      next_touch_time: undefined,
      meeting_time: undefined,
      account_executive_id: undefined,
      note: note || "",
      controlKey: undefined,
      internalDispositionId: undefined,
    }),
    [note]
  );

  const isDispoTypeSet = useMemo(() => {
    const currentContact = contacts?.find((c) => contact?.id === c?.id);

    if (!currentContact) return false;

    return !!currentContact?.contact_disposition_type;
  }, [contact?.id, contacts]);

  const DispositionSubmissionHookParams = useMemo(
    () => ({
      isDispoConversation,
      nextTouchTimeCustom: customNextTouchTime,
      onSuccess: (data: FormValidationSchemaI) => {
        onSubmit?.(data);
      },
    }),
    [isDispoConversation, customNextTouchTime]
  );
  const {
    handleDipositionSubmission,
    isPending: isDispositionSubmissionPending,
  } = useDispositionSubmission(DispositionSubmissionHookParams);

  /**
   * Fetches email follow-up configuration based on disposition selection
   *
   * When disposition changes or conversation status updates:
   * 1. Builds parameters with conversation status and disposition
   * 2. If there's a conversation but no disposition, clears email config
   * 3. Makes API call to check if email should be sent
   * 4. Updates email config based on API response
   * 5. Logs success/failure for monitoring
   */
  useEffect(() => {
    if (emailFollowUpRequestParams?.has_meeting) return;

    const parameters: GetShouldSendEmailRequestParamsI = {
      had_conversation_with_prospect: isDispoConversation,
    };

    if (isDispoConversation && !observalbeValues?.disposition) {
      onChangeEmailFollowUpConfig?.(undefined);
      return;
    }

    parameters.disposition = observalbeValues?.disposition;

    (async () => {
      const resp = await shouldSendFollowUpEmailAsync({
        campaignId: campaign?.id as string,
        callId: call?.customParameters.get("call_id") as string,
        parameters,
      });

      const emailConfig = resp?.data;

      if (resp.status !== 200) {
        dd.log(
          `${LOG_CATEGORIES.FOLLOW_UP_EMAIL}[DISPOSITION_SECTION] - failed to get email config`,
          {
            data: { resp },
          }
        );

        onChangeEmailFollowUpConfig?.(undefined);
        return;
      }

      if (resp.status === 200) {
        dd.log(
          `${LOG_CATEGORIES.FOLLOW_UP_EMAIL}[DISPOSITION_SECTION] - disposition selected`,
          {
            data: { email_config: emailConfig },
          }
        );

        onChangeEmailFollowUpConfig?.(emailConfig);
      }
    })();
  }, [
    observalbeValues?.disposition,
    observalbeValues?.disposition_type,
    isDispoConversation,
  ]);

  return (
    <section className={clsxMerge("w-full", className)}>
      <DispositionConversationRow
        className="mb-2"
        value={isDispoConversation}
        onChange={(value) => {
          setIsDispoConversation(value);
        }}
      />

      <Formik
        enableReinitialize
        validationSchema={
          isDispoConversation
            ? isDispoTypeSet
              ? FormDispositionNurtureValidationSchema
              : FormDispositionValidationSchema
            : FormDefaultValidationSchema
        }
        initialValues={formInitialValues}
        onSubmit={handleDipositionSubmission}
      >
        {({
          errors,
          touched,
          isValid,
          isSubmitting,
          values,
          setValues,
          handleSubmit,
        }) => (
          <Form className="flex grow flex-col">
            <DropdownsValueSetter
              values={values}
              setValues={setValues}
              onChange={setObservalbeValues}
              isDispositionType={isDispoTypeSet}
              isDisposition={isDispoConversation}
              setCustomNextTouchTime={setCustomNextTouchTime}
            />

            {typeof isDispoConversation === "boolean" &&
              !isDispoConversation &&
              noConversationDispositionOptions.length > 1 && (
                <DispositionRow
                  key={isDispoConversation.toString()}
                  names={{
                    DISPOSITION: "disposition",
                  }}
                  disposition={values.disposition}
                  dispositionOptions={noConversationDispositionOptions}
                  errors={errors}
                  touched={touched}
                  customNextTouchTime={customNextTouchTime}
                  className="mb-6"
                  setValues={setValues}
                />
              )}

            {typeof isDispoConversation === "boolean" &&
              isDispoConversation && (
                <>
                  {!isDispoTypeSet && (
                    <DispositionDecisionMakerRow
                      name="disposition_type"
                      errors={errors.disposition_type}
                      touched={touched.disposition_type}
                      className="mb-3"
                    />
                  )}

                  {((!!values.disposition_type &&
                    isDispositionRequired(values.disposition_type as string)) ||
                    isDispoTypeSet) && (
                    <DispositionRow
                      key={isDispoConversation.toString()}
                      names={{
                        DISPOSITION: "internalDispositionId",
                        NEXT_TOUCH_TIME: "next_touch_time",
                        MEETING_TIME: "meeting_time",
                        MEETING_TAKER: "account_executive_id",
                      }}
                      disposition={values.disposition}
                      dispositionOptions={withConversationDispositionOptions}
                      errors={errors}
                      touched={touched}
                      customNextTouchTime={customNextTouchTime}
                      className="mb-6"
                      setValues={setValues}
                    />
                  )}

                  {isDisqualifiedDisposition(values.disposition_type) && (
                    <DispositionRow
                      key={`${isDispoConversation.toString()}-disqualified`}
                      names={{
                        DISPOSITION: "internalDispositionId",
                      }}
                      disposition={values.disposition}
                      dispositionOptions={disqualifiedDispositionOptions}
                      errors={errors}
                      touched={touched}
                      className="mb-6"
                      setValues={setValues}
                    />
                  )}
                </>
              )}

            {!isDispositionRequired(values.disposition_type as string) && (
              <DispositionSectionSearchRow />
            )}

            <DispositionNoteRow
              name="note"
              errors={errors.note}
              touched={touched.note}
            />

            <DispositionActionsRow
              actionControlKeys={actionControlKeys}
              buttonTexts={buttonTexts}
              values={values}
              setValues={setValues}
              isValid={typeof isDispoConversation === "boolean" && isValid}
              isSubmitting={isSubmitting || isDispositionSubmissionPending}
              onSubmit={handleSubmit}
            />
          </Form>
        )}
      </Formik>
    </section>
  );
};

export const DispositionSection = ({
  className,
  onSubmit,
}: DispositionSectionPropsI) => {
  const { onAccountUpdate } = useAccountDetailsContext();
  const { setCall } = useCallingContext();
  const { clear: clearInCallContext } = useInCallContext();
  const { setIsEmailSection, setEmailFollowUpData } =
    useAccountDetailsContext();

  const [emailFollowUpConfig, setEmailFollowUpConfig] =
    useState<EmailFollowUpConfigByDispositionI>();

  const emailFollowUpConfigRef = useRef<
    EmailFollowUpConfigByDispositionI | undefined
  >(emailFollowUpConfig);

  const handlerOnSubmit = async (data: FormValidationSchemaI) => {
    const emailFollowUpConfig = emailFollowUpConfigRef.current;

    dispatchCustomEvent<DispositionSubmitEventDataI>({
      eventType: CUSTOM_EVENTS.ACCOUNT.DISPOSITION.SUBMIT,
      data: { disposition: data },
    });

    if (emailFollowUpConfig?.should_send_email) {
      setIsEmailSection?.(true);
      setEmailFollowUpData?.(emailFollowUpConfig);
      onAccountUpdate();

      return;
    }

    setCall(undefined);
    clearInCallContext();
    onAccountUpdate();

    if (data?.controlKey === DISPOSITION_CONTROL_KEYS.CONTINUE_DIALING_LIST) {
      widgets.close({ id: WIDGETS.MAXED_DIALER });
    }
  };

  const handleOnChangeEmailFollowUpConfig = (
    emailConfig?: EmailFollowUpConfigByDispositionI
  ) => {
    emailFollowUpConfigRef.current = emailConfig;
    setEmailFollowUpConfig(emailConfig);
  };

  return (
    <AccountDetailsSectionContainer
      title={
        <DispositionSectionHeader
          isEmailReady={
            !!(
              emailFollowUpConfig?.should_send_email &&
              (emailFollowUpConfig?.email_subject ||
                emailFollowUpConfig?.email_body)
            )
          }
        />
      }
      className={clsxMerge(
        "border border-[#4474E3]",
        "animate-pulse-alt",
        className
      )}
      style={
        {
          "--pulse-alt-start-color": "rgba(68,116,227,.7)",
          "--pulse-alt-end-color": "rgba(0,0,0,0)",
        } as CSSProperties
      }
    >
      <DispositionSectionContextProvider>
        <DispositionSectionUI
          buttonTexts={["Save", "Submit & continue"]}
          actionControlKeys={[
            "",
            DISPOSITION_CONTROL_KEYS.CONTINUE_DIALING_LIST,
          ]}
          onSubmit={onSubmit || handlerOnSubmit}
          onChangeEmailFollowUpConfig={handleOnChangeEmailFollowUpConfig}
        />
      </DispositionSectionContextProvider>
    </AccountDetailsSectionContainer>
  );
};
